// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  VirtualNetworkAppliance,
  VirtualNetworkAppliancesListAllOptionalParams,
  VirtualNetworkAppliancesListOptionalParams,
  VirtualNetworkAppliancesDeleteOptionalParams,
  VirtualNetworkAppliancesDeleteResponse,
  VirtualNetworkAppliancesGetOptionalParams,
  VirtualNetworkAppliancesGetResponse,
  VirtualNetworkAppliancesCreateOrUpdateOptionalParams,
  VirtualNetworkAppliancesCreateOrUpdateResponse,
  TagsObject,
  VirtualNetworkAppliancesUpdateTagsOptionalParams,
  VirtualNetworkAppliancesUpdateTagsResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a VirtualNetworkAppliances. */
export interface VirtualNetworkAppliances {
  /**
   * Gets all virtual network appliances in a subscription.
   * @param options The options parameters.
   */
  listAll(
    options?: VirtualNetworkAppliancesListAllOptionalParams,
  ): PagedAsyncIterableIterator<VirtualNetworkAppliance>;
  /**
   * Gets all virtual network appliances in a resource group.
   * @param resourceGroupName The name of the resource group.
   * @param options The options parameters.
   */
  list(
    resourceGroupName: string,
    options?: VirtualNetworkAppliancesListOptionalParams,
  ): PagedAsyncIterableIterator<VirtualNetworkAppliance>;
  /**
   * Deletes the specified virtual network appliance.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkApplianceName The name of the virtual network appliance.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    virtualNetworkApplianceName: string,
    options?: VirtualNetworkAppliancesDeleteOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkAppliancesDeleteResponse>,
      VirtualNetworkAppliancesDeleteResponse
    >
  >;
  /**
   * Deletes the specified virtual network appliance.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkApplianceName The name of the virtual network appliance.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    virtualNetworkApplianceName: string,
    options?: VirtualNetworkAppliancesDeleteOptionalParams,
  ): Promise<VirtualNetworkAppliancesDeleteResponse>;
  /**
   * Gets information about the specified virtual network appliance.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkApplianceName The name of the virtual network appliance.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    virtualNetworkApplianceName: string,
    options?: VirtualNetworkAppliancesGetOptionalParams,
  ): Promise<VirtualNetworkAppliancesGetResponse>;
  /**
   * Creates or updates a virtual network appliance.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkApplianceName The name of the virtual network appliance.
   * @param parameters Parameters supplied to the create or update virtual network appliance operation.
   * @param options The options parameters.
   */
  beginCreateOrUpdate(
    resourceGroupName: string,
    virtualNetworkApplianceName: string,
    parameters: VirtualNetworkAppliance,
    options?: VirtualNetworkAppliancesCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<VirtualNetworkAppliancesCreateOrUpdateResponse>,
      VirtualNetworkAppliancesCreateOrUpdateResponse
    >
  >;
  /**
   * Creates or updates a virtual network appliance.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkApplianceName The name of the virtual network appliance.
   * @param parameters Parameters supplied to the create or update virtual network appliance operation.
   * @param options The options parameters.
   */
  beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    virtualNetworkApplianceName: string,
    parameters: VirtualNetworkAppliance,
    options?: VirtualNetworkAppliancesCreateOrUpdateOptionalParams,
  ): Promise<VirtualNetworkAppliancesCreateOrUpdateResponse>;
  /**
   * Updates a virtual network appliance tags.
   * @param resourceGroupName The name of the resource group.
   * @param virtualNetworkApplianceName The name of the virtual network appliance.
   * @param parameters Parameters supplied to update virtual network appliance tags.
   * @param options The options parameters.
   */
  updateTags(
    resourceGroupName: string,
    virtualNetworkApplianceName: string,
    parameters: TagsObject,
    options?: VirtualNetworkAppliancesUpdateTagsOptionalParams,
  ): Promise<VirtualNetworkAppliancesUpdateTagsResponse>;
}
